import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faList } from "@fortawesome/free-solid-svg-icons/faList";
import { faCircleXmark } from "@fortawesome/free-solid-svg-icons/faCircleXmark";
import { styled, useTheme } from "@mui/system";
import Badge, { BadgeProps } from "@mui/material/Badge";

import Stack from "@vapor/react-material/Stack";
import Box from "@vapor/react-material/Box";
import Button from "@vapor/react-material/Button";
import { ExtendedTypography as Typography } from "@vapor/react-extended/ExtendedTypography";

export type VaporEmptyStateProps = {
  children?: React.ReactNode;
  /**
   * The title is used to display the main informations (usually the cause of the Empty State).
   */
  title: string;
  /**
   * Description is used to convey further information to detail the cause of the empty state and, eventually, how to get to display at least an item.
   */
  description?: string;
  /**
   * If badgeIcon is `true` the feedback icon displayed in blue is shown.
   */
  badgeIcon?: boolean;
  /**
   * Props that you want to pass to Primary Button
   */
  primaryButtonProps?: object;
  /**
   * Props that you want to pass to Primary Button
   */
  secondaryButtonProps?: object;
};

const StyledBadge = styled(Badge)<BadgeProps>(() => ({
  "& .MuiBadge-badge": {
    right: 0,
    top: 85
  }
}));

export const VaporEmptyState = React.forwardRef<
  HTMLDivElement,
  VaporEmptyStateProps
>((props, ref) => {
  const {
    title,
    description,
    badgeIcon,
    primaryButtonProps,
    secondaryButtonProps,
    ...otherProps
  } = props;
  const theme = useTheme();
  return (
    <Stack
      ref={ref}
      {...otherProps}
      style={{ justifyContent: "center", textAlign: "center" }}
    >
      <Box>
        <StyledBadge
          badgeContent={
            badgeIcon ? (
              <FontAwesomeIcon
                icon={faCircleXmark}
                style={{
                  color: theme.palette.primary.textTitleColor,
                  background: theme.palette.primary.surfaceDefault,
                  fontSize: 48,
                  borderRadius: "50%"
                }}
              />
            ) : null
          }
        >
          <FontAwesomeIcon
            icon={faList}
            style={{ color: "#E4EAF0", fontSize: 101 }}
          />
        </StyledBadge>
      </Box>
      <Stack sx={{ mt: 2, textAlign: "center" }} spacing={1}>
        <Typography variant="titleMedium" color="primary.textTitleColor">
          {title}
        </Typography>
        <Typography variant="body">{description || null}</Typography>
      </Stack>
      <Stack
        direction="row"
        sx={{ mt: 2, textAlign: "center", display: "flow-root" }}
        spacing={2}
      >
        {primaryButtonProps && (
          <Button variant="contained" {...primaryButtonProps} />
        )}
        {secondaryButtonProps && (
          <Button variant="outlined" {...secondaryButtonProps} />
        )}
      </Stack>
    </Stack>
  );
});
